import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/types/graph';
import config from '@/config';
import { useSurveyURL } from '@/hooks/useSurveyURL';
import { setSurveyRatingSent } from '@/reducers/graph';
import { AnimatedProfessionalAvatar } from '@/components/AnimatedProfessionalAvatar';
import { useSavePatientFeedback } from '@/hooks/usePatientFeedback';
import { useStatistics } from '@/hooks/useStatistics';
import { PatientChecklist } from '@/pages/offboarding/med-g/PatientChecklist';
import { FakeDataGatheringAnimation } from '@/pages/offboarding/med-g/FakeDataGatheringAnimation';
import { RatingModal } from '@/pages/offboarding/med-g/RatingModal';
import { SurveyDrawer } from '@/pages/offboarding/med-g/SurveyDrawer';
import { DemoNextSteps } from '@/pages/offboarding/med-g/DemoNextSteps';
import { isWebsiteDemoPractice } from '@/util/demoPractice';
import { useQuestionnairePages } from '@/hooks/useQuestionnairePages';
import { isMmgVariant } from '@/util/mmg';
import { MmgConfirmation } from '@/pages/offboarding/MmgConfirmation';

export const Confirmation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedRating, setSelectedRating] = useState<number | undefined>(
    undefined,
  );
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [showFakeDataGatheringAnimation, setShowFakeDataGatheringAnimation] =
    useState(true);
  const [fadeIn, setFadeIn] = useState(false);
  const MAX_SCORE = 7;
  const isDemoSession = useSelector((state: RootState) =>
    isWebsiteDemoPractice(state.consultation.practice?.slug),
  );
  const professional = useSelector(
    (state: RootState) => state.consultation.professional,
  );
  // mmg (patient-phone walk-in) reuses this DEFAULT offboarding flow, so detect
  // the MMG variant and hand off to the dedicated MMG check-in confirmation
  // rather than the regular doctor/checklist confirmation (ALDO-2540). Read the
  // active questionnaire variant (graph.variant) rather than consultation.variant:
  // the public `?variant=` QR/practice-link path sets the former but not the
  // latter.
  const isMmg = useSelector((state: RootState) =>
    isMmgVariant(state.graph.variant),
  );
  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );
  const questionnaireId = useSelector(
    (state: RootState) => state.questionnaireId,
  );

  const surveyRatingSent = useSelector(
    (state: RootState) => state.questionnaire.surveyRatingSent,
  );
  const { isLastPage } = useQuestionnairePages();

  const surveyUrl = useSurveyURL({
    page: 'end',
    maxScore: MAX_SCORE,
    rating: selectedRating,
  });
  const { mutate: savePatientFeedback } = useSavePatientFeedback();
  const recordStatistics = useStatistics();

  useEffect(() => {
    if (!isLastPage) navigate('/questionnaire');
  }, [currentPage, isLastPage, navigate]);

  useEffect(() => {
    if (!showFakeDataGatheringAnimation) {
      const timer = setTimeout(() => {
        setFadeIn(true);
      }, 500); // this allows time to load the picture
      return () => clearTimeout(timer);
    }
  }, [showFakeDataGatheringAnimation]);

  useEffect(() => {
    if (isDemoSession || surveyRatingSent || showFakeDataGatheringAnimation) {
      return;
    }
    const timer = setTimeout(() => {
      setShowSurvey(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [isDemoSession, showFakeDataGatheringAnimation, surveyRatingSent]);

  const handleRatingPress = (rating: number) => {
    setSelectedRating(rating);
    if (questionnaireId && rating) {
      recordStatistics({ section: 'RATING_SUBMITTED' });
      savePatientFeedback({
        rating: rating,
        maxScore: MAX_SCORE,
        questionnaireId: questionnaireId,
      });
      dispatch(setSurveyRatingSent(true));
      // Close the drawer as the modal opens: a drawer left open underneath
      // keeps its overlay on top of the modal and swallows the first tap.
      setShowSurvey(false);
      setShowRatingModal(true);
    }
  };

  const handleSurveyClick = () => {
    if (selectedRating && selectedRating >= 3) {
      recordStatistics({ section: 'TRUSTPILOT_REVIEW_CLICKED' });
      window.open(config.trustpilotUrl, '_blank');
    } else {
      window.open(surveyUrl, '_blank');
    }
  };

  // MMG (on-call walk-in) gets its own dedicated check-in confirmation instead
  // of the doctor/checklist + survey flow (ALDO-2540).
  if (isMmg) {
    return <MmgConfirmation />;
  }

  if (showFakeDataGatheringAnimation) {
    return (
      <FakeDataGatheringAnimation
        onComplete={() => setShowFakeDataGatheringAnimation(false)}
      />
    );
  }

  return (
    <div
      className={`w-full h-full flex flex-col items-center justify-center text-base transition-opacity duration-600 ease-out px-4 n2xs:px-6 nsm:px-0 ${
        fadeIn ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="flex flex-col items-center nsm:justify-center mt-6 nsm:mt-0 h-full sm:w-full overflow-y-auto no-scrollbar">
        <div className="w-full max-w-md flex flex-col items-center text-center justify-between">
          <div className="grow flex flex-col justify-center">
            <div
              className={`self-center flex flex-col shrink items-center justify-center z-10 transition-all duration-500 ease-out ${
                fadeIn ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              }`}
            >
              <AnimatedProfessionalAvatar
                professional={professional}
                size={160}
              />
            </div>
            <div
              className={`flex flex-col justify-start gap-4 mt-6 transition-all duration-500 ease-out delay-300 ${
                fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {isDemoSession ? (
                <>
                  <p className="text-xl font-medium mt-2">
                    Votre synthèse médicale est prête
                  </p>
                  <p className="text-indigo-900 font-light mb-4">
                    Vous la recevrez par email rapidement
                  </p>
                </>
              ) : (
                <p className="text-xl font-medium mb-4">
                  {`Vos réponses ont bien été`}
                  <br />
                  {`transmises à votre médecin`}
                </p>
              )}
            </div>

            <div
              className={`transition-all duration-500 ease-out delay-[600ms] ${
                fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {isDemoSession ? <DemoNextSteps /> : <PatientChecklist />}
            </div>
          </div>
          {!isDemoSession && (
            <p className=" mt-10 text-indigo-900 font-light">
              {' '}
              Vous pouvez fermer la fenêtre
            </p>
          )}
        </div>

        {config.surveyUrl && !isDemoSession && (
          <SurveyDrawer
            showSurvey={showSurvey}
            setShowSurvey={setShowSurvey}
            maxScore={MAX_SCORE}
            handleRatingPress={handleRatingPress}
          />
        )}
      </div>

      {showRatingModal && (
        <RatingModal
          showRatingModal={showRatingModal}
          setShowRatingModal={setShowRatingModal}
          setShowSurvey={setShowSurvey}
          selectedRating={selectedRating}
          handleSurveyClick={handleSurveyClick}
        />
      )}
    </div>
  );
};
